import { get_token } from "@/router";

function normalize_text(value) {
    return `${value || ""}`.trim();
}

function parse_user_id(raw_user_id) {
    const user_id_text = String(raw_user_id || "").trim();
    const user_id = get_numeric_user_id(user_id_text);

    return is_valid_user_id(user_id) ? user_id : null;
}

function get_numeric_user_id(user_id_text) {
    return /^\d+$/.test(user_id_text) ? Number(user_id_text) : null;
}

function is_valid_user_id(user_id) {
    return Number.isSafeInteger(user_id) && user_id > 0;
}

function get_url_with_slash(url) {
    const normalized_url = String(url || "").trim();
    return normalized_url.endsWith("/") ? normalized_url : `${normalized_url}/`;
}

function get_endpoint_url(base_url, user_id, action = "") {
    const safe_user_id = parse_user_id(user_id);

    if (!safe_user_id || !base_url) return "";

    const safe_action = action === "update" ? "update/" : "";
    return `${get_url_with_slash(base_url)}${safe_user_id}/${safe_action}`;
}

function get_first_available_value(source, keys) {
    return keys.reduce((result, key) => result || source?.[String(key)], null);
}

function get_error_data(response_data) {
    return get_first_available_value(response_data, ["errors", "detail", "error", "message"]) || response_data;
}

function format_object_error(error_data) {
    return Object.entries(error_data)
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`)
        .join(" | ");
}

function get_error_message(response_data, fallback_message) {
    const error_data = get_error_data(response_data);
    const formatters = {
        string: (value) => value,
        object: (value) => Array.isArray(value) ? value.join(", ") : format_object_error(value)
    };
    const formatter = error_data ? formatters[typeof error_data] : null;

    return formatter ? formatter(error_data) : fallback_message;
}

async function get_response_data(response, fallback_message) {
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(get_error_message(data, fallback_message));
    }

    return data;
}

function get_auth_headers(include_json_content_type = true) {
    const auth_token = get_token();
    const headers = {
        "Accept": "application/json"
    };

    if (include_json_content_type) headers["Content-Type"] = "application/json";
    if (auth_token) headers.Authorization = `Token ${auth_token}`;

    return headers;
}

function extract_collection(response_data, collection_keys = ["data", "results", "users"]) {
    if (Array.isArray(response_data)) return response_data;
    if (!response_data || typeof response_data !== "object") return [];

    return collection_keys.reduce((collection, key) => {
        const value = response_data[String(key)];

        return collection.length ? collection : extract_collection(value, collection_keys);
    }, []);
}

function find_user_by_id(response_data, user_id) {
    const safe_user_id = parse_user_id(user_id);
    const users = extract_collection(response_data);
    const direct_user_id = parse_user_id(response_data?.id || response_data?.user_id);

    return users.find((user) => parse_user_id(user?.id || user?.user_id) === safe_user_id)
        || (direct_user_id === safe_user_id ? response_data : null);
}

function get_selected_user_from_storage() {
    return JSON.parse(sessionStorage.getItem("edit_user_profile") || "null");
}

function get_config_warning(context) {
    const warnings = {
        managed: "VUE_APP_USER_MANAGEMENT_URL is not configured.",
        self: "VUE_APP_VIEW_USER_URL or VUE_APP_USER_MANAGEMENT_URL is not configured."
    };

    return warnings[String(context)] || "";
}

function get_missing_update_url_error(component) {
    const context = get_update_url_context(component);
    const is_missing_url = !get_update_base_url(component, context);

    if (is_missing_url) console.warn(get_config_warning(context));

    return is_missing_url ? "Unable to save user details." : "";
}

function get_update_url_context(component) {
    return component.is_managed_user_edit ? "managed" : "self";
}

function get_update_base_url(component, context) {
    const urls = {
        managed: component.user_management_url,
        self: component.view_user_url
    };

    return urls[String(context)] || "";
}

function build_json_request(url, body) {
    return new Request(url, {
        method: "POST",
        headers: get_auth_headers(true),
        body: JSON.stringify(body)
    });
}

async function send_request(request) {
    const http_client = window.fetch.bind(window);
    return http_client(request);
}

function is_stored_user_match(stored_user, safe_user_id) {
    const stored_user_id = parse_user_id(stored_user && (stored_user.id || stored_user.user_id));
    return Boolean(stored_user) && stored_user_id === safe_user_id;
}

function can_load_profile_phone(component, safe_user_id) {
    return Boolean(safe_user_id && component.update_user_url && get_token());
}

async function fetch_profile_phone(component, safe_user_id) {
    const request = new Request(get_endpoint_url(component.update_user_url, safe_user_id), {
        method: "GET",
        headers: get_auth_headers(false)
    });
    const response = await send_request(request);
    return response.status === 200 ? response.json() : {};
}

export default {
    set_profile_edit(profile = {}) {
        this.form.first_name = normalize_text(profile.first_name);
        this.form.last_name = normalize_text(profile.last_name);
        this.form.email = normalize_text(profile.email);
        this.form.phone = profile.phone_number || "";
    },

    parse_user_id,
    get_endpoint_url,
    get_auth_headers,
    extract_collection,
    find_user_by_id,
    get_response_data,
    build_json_request,

    get_selected_profile_id(profile) {
        return this.selected_user_id || parse_user_id(profile && (profile.id || profile.user_id));
    },

    get_selected_user_from_storage,

    get_return_path() {
        return this.$route.query.return_to === "user-management" ? "/user-management" : "/profile";
    },

    async load_managed_user(user_id) {
        const safe_user_id = parse_user_id(user_id);
        const stored_user = get_selected_user_from_storage();

        if (is_stored_user_match(stored_user, safe_user_id)) return stored_user;

        throw new Error("Unable to load user details.");
    },

    async load_profile_phone(profile, user_id) {
        const safe_user_id = parse_user_id(user_id);

        if (!can_load_profile_phone(this, safe_user_id)) return profile;

        try {
            const response_data = await fetch_profile_phone(this, safe_user_id);
            const profile_data = find_user_by_id(response_data, safe_user_id) || response_data;

            profile.phone_number = profile_data.phone_number;
        } catch (error) {
            console.log(`Failed to fetch phone number: ${error}`);
        }

        return profile;
    },

    get_user_update_url(safe_user_id) {
        const base_url = this.is_managed_user_edit ? this.user_management_url : this.view_user_url;
        return get_endpoint_url(base_url, safe_user_id, "update");
    },

    get_update_context_error(context) {
        const has_missing_context = !context.safe_user_id || !context.auth_token || !context.user_update_url;

        return has_missing_context
            ? "Unable to save user details."
            : get_missing_update_url_error(this);
    },

    async save_user_details(user_update_url) {
        const user_request = build_json_request(user_update_url, {
            "first_name": this.form.first_name,
            "last_name": this.form.last_name
        });
        const user_response = await send_request(user_request);

        await get_response_data(user_response, "Unable to save user.");
    },

    async save_profile_details(profile_update_url) {
        if (!profile_update_url) return;

        const profile_request = build_json_request(profile_update_url, {
            "phone_number": this.form.phone || ""
        });
        const profile_response = await send_request(profile_request);

        await get_response_data(profile_response, "Unable to save user profile.");
    }
};
